import {
  PlaygroundLocalMsg,
  usePlaygroundLocalMsg,
} from "@faire/prototype-playground/platform/localization";
import { cn } from "@faire/react/tools/cn";
import { ArrowRight } from "@faire/slate-icons/ArrowRight";
import { CheckOutline } from "@faire/slate-icons/CheckOutline";
import { CreditCard } from "@faire/slate-icons/CreditCard";
import { PriceTagDollar } from "@faire/slate-icons/PriceTagDollar";
import { Synchronized } from "@faire/slate-icons/Synchronized";
import { Badge } from "@faire/slate/components/badge";
import { Button } from "@faire/slate/components/button";
import { Tag } from "@faire/slate/components/tag";
import type {
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
} from "react";
import { useState } from "react";

import {
  Clover,
  FairePaySquare,
  InsiderKey40,
  InsiderLogoLargeDefault,
  ShopifyColored,
  Square,
} from "./ProgramIcons";

export const BENEFITS_MENU_ID = "faire-benefits-menu";

type BenefitId = "insider" | "fairePay" | "terms" | "pos" | "offers";

export type BenefitsMenuLayoutVariant = 1 | 2 | 3 | 4 | 5;

interface BenefitMenuItem {
  id: BenefitId;
  icon: ReactNode;
  iconTone: string;
  shortTitle: ReactNode;
  title: ReactNode;
  description: ReactNode;
  count?: number;
}

interface BenefitsMegaMenuProps {
  isOpen: boolean;
  layoutVariant: BenefitsMenuLayoutVariant;
  onBlur: FocusEventHandler<HTMLDivElement>;
  onFocus: FocusEventHandler<HTMLDivElement>;
  onMouseEnter: MouseEventHandler<HTMLDivElement>;
  onMouseLeave: MouseEventHandler<HTMLDivElement>;
  onNavigate: () => void;
  onRequestClose: () => void;
}

export const BenefitsMegaMenu = ({
  isOpen,
  layoutVariant,
  onBlur,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  onNavigate,
  onRequestClose,
}: BenefitsMegaMenuProps): ReactNode => {
  const [activeBenefitId, setActiveBenefitId] = useState<BenefitId>("insider");
  const menuLabel = usePlaygroundLocalMsg("Faire growth tools");
  const benefitsListLabel = usePlaygroundLocalMsg("Growth tools and programs");

  if (!isOpen) {
    return null;
  }

  const menuItems: BenefitMenuItem[] = [
    {
      id: "insider",
      shortTitle: <PlaygroundLocalMsg defaultMessage="Insider" />,
      title: (
        <PlaygroundLocalMsg defaultMessage="Free shipping and member discounts" />
      ),
      description: <PlaygroundLocalMsg defaultMessage="Insider" />,
      iconTone: "bg-fs-program-surface-insider-primary",
      icon: (
        <InsiderKey40
          className="h-(--slate-dimensions-7) w-(--slate-dimensions-7)"
          height="28"
          width="28"
        />
      ),
    },
    {
      id: "fairePay",
      shortTitle: <PlaygroundLocalMsg defaultMessage="Faire Pay" />,
      title: (
        <PlaygroundLocalMsg defaultMessage="3% cash back on every order" />
      ),
      description: <PlaygroundLocalMsg defaultMessage="Faire Pay" />,
      iconTone: "bg-fs-program-surface-faire_pay-primary",
      icon: <FairePaySquare height="32" width="32" />,
    },
    {
      id: "terms",
      shortTitle: <PlaygroundLocalMsg defaultMessage="Terms" />,
      title: (
        <PlaygroundLocalMsg defaultMessage="Up to $30K terms · Buy now, pay later" />
      ),
      description: <PlaygroundLocalMsg defaultMessage="Open with Faire" />,
      iconTone: "bg-fs-expressive-surface-blue",
      icon: (
        <CreditCard
          className="text-fs-icon-primary h-(--slate-dimensions-7) w-(--slate-dimensions-7)"
          height="28"
          width="28"
        />
      ),
    },
    {
      id: "pos",
      shortTitle: <PlaygroundLocalMsg defaultMessage="POS integrations" />,
      title: <PlaygroundLocalMsg defaultMessage="Sync orders with your POS" />,
      description: (
        <PlaygroundLocalMsg defaultMessage="Shopify, Square & more" />
      ),
      iconTone: "bg-fs-expressive-surface-green",
      icon: (
        <Synchronized
          className="text-fs-icon-primary h-(--slate-dimensions-7) w-(--slate-dimensions-7)"
          height="28"
          width="28"
        />
      ),
    },
    {
      id: "offers",
      shortTitle: <PlaygroundLocalMsg defaultMessage="Offers" />,
      title: <PlaygroundLocalMsg defaultMessage="Your offers" />,
      description: (
        <PlaygroundLocalMsg defaultMessage="Offers available to you" />
      ),
      count: 3,
      iconTone: "bg-fs-expressive-surface-yellow",
      icon: (
        <PriceTagDollar
          className="text-fs-icon-primary h-(--slate-dimensions-7) w-(--slate-dimensions-7)"
          height="28"
          width="28"
        />
      ),
    },
  ];

  const layout = MENU_LAYOUTS[layoutVariant];

  const handleTabKeyDown: KeyboardEventHandler<HTMLButtonElement> = (event) => {
    const currentIndex = menuItems.findIndex(
      (item) => item.id === activeBenefitId
    );
    const lastIndex = menuItems.length - 1;
    let nextIndex = currentIndex;

    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
    } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = lastIndex;
    } else {
      return;
    }

    event.preventDefault();
    const nextItem = menuItems[nextIndex];
    setActiveBenefitId(nextItem.id);
    document.getElementById(`benefit-tab-${nextItem.id}`)?.focus();
  };

  return (
    <div
      className={menuPositionCls}
      onBlur={onBlur}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          onRequestClose();
        }
      }}
    >
      <span aria-hidden className={menuAnchorCls} />
      <section
        id={BENEFITS_MENU_ID}
        aria-label={menuLabel}
        className={cn(menuPanelBaseCls, layout.panelCls)}
        role="dialog"
      >
        <div className={cn(menuListSectionBaseCls, layout.listSectionCls)}>
          <div className="flex flex-col gap-(--slate-spacing-group-xsmall)">
            <h2 className={layout.listHeadingCls}>
              <PlaygroundLocalMsg defaultMessage="Growth" />
            </h2>
            {layout.showListIntroduction ? (
              <p className="type-fs-label text-fs-text-subdued">
                <PlaygroundLocalMsg defaultMessage="Tools to help your business grow." />
              </p>
            ) : null}
          </div>

          <div aria-label={benefitsListLabel} role="tablist">
            {menuItems.map((item) => {
              const isActive = item.id === activeBenefitId;

              return (
                <button
                  key={item.id}
                  id={`benefit-tab-${item.id}`}
                  aria-controls={
                    isActive ? `benefit-panel-${item.id}` : undefined
                  }
                  aria-selected={isActive}
                  className={cn(
                    menuItemCls,
                    isActive ? layout.activeMenuItemCls : "",
                    {
                      "gap-(--slate-spacing-element-default) px-(--slate-spacing-element-default) py-(--slate-spacing-element-small)":
                        layout.compactListItems,
                    }
                  )}
                  role="tab"
                  tabIndex={isActive ? 0 : -1}
                  type="button"
                  onClick={() => setActiveBenefitId(item.id)}
                  onFocus={() => setActiveBenefitId(item.id)}
                  onKeyDown={handleTabKeyDown}
                  onMouseEnter={() => setActiveBenefitId(item.id)}
                >
                  <span
                    className={cn(
                      menuItemIconCls,
                      item.iconTone,
                      layout.menuItemIconCls
                    )}
                  >
                    {item.icon}
                  </span>
                  <span className="min-w-0 flex-1 text-left">
                    <span className={layout.menuItemTitleCls}>
                      {layout.useShortMenuTitles ? item.shortTitle : item.title}
                      {item.count ? (
                        <span className="ml-(--slate-spacing-element-small) inline-flex align-middle">
                          <Badge value={item.count} />
                        </span>
                      ) : null}
                    </span>
                    {layout.showMenuItemDescription ? (
                      <span className="type-fs-label text-fs-text-subdued block">
                        {item.description}
                      </span>
                    ) : null}
                  </span>
                  {layout.showMenuItemArrow ? (
                    <ArrowRight
                      className={cn(
                        "h-(--slate-dimensions-4) w-(--slate-dimensions-4) shrink-0",
                        isActive
                          ? "text-fs-icon-primary"
                          : "text-fs-icon-subdued"
                      )}
                      height="16"
                      width="16"
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className="min-w-0">
          <BenefitDetailPanel
            activeBenefitId={activeBenefitId}
            layoutVariant={layoutVariant}
            onNavigate={onNavigate}
          />
        </div>
      </section>
    </div>
  );
};

const BenefitDetailPanel = ({
  activeBenefitId,
  layoutVariant,
  onNavigate,
}: {
  activeBenefitId: BenefitId;
  layoutVariant: BenefitsMenuLayoutVariant;
  onNavigate: () => void;
}): ReactNode => {
  if (activeBenefitId === "fairePay") {
    return (
      <ProgramDetail
        id="fairePay"
        eyebrow={<PlaygroundLocalMsg defaultMessage="Faire Pay" />}
        title={
          <PlaygroundLocalMsg defaultMessage="Faire Pay is built for wholesale buying" />
        }
        description={
          <PlaygroundLocalMsg defaultMessage="Get 3% off every Faire purchase paid with Faire Pay, directly increasing your profit." />
        }
        ctaLabel="Activate to start saving"
        ctaUrl="https://www.faire.com/faire-pay"
        heroTone="bg-fs-program-surface-faire_pay-primary"
        layoutVariant={layoutVariant}
        logo={<FairePaySquare height="48" width="48" />}
        onNavigate={onNavigate}
        highlights={
          <>
            <DetailHighlight
              title={<PlaygroundLocalMsg defaultMessage="3% off" />}
              description={
                <PlaygroundLocalMsg defaultMessage="Every Faire checkout paid with Faire Pay" />
              }
            />
            <DetailHighlight
              title={<PlaygroundLocalMsg defaultMessage="One statement" />}
              description={
                <PlaygroundLocalMsg defaultMessage="A single monthly balance instead of invoice-by-invoice payments" />
              }
            />
            <DetailHighlight
              title={<PlaygroundLocalMsg defaultMessage="More time to pay" />}
              description={
                <PlaygroundLocalMsg defaultMessage="Flexible payments with transparent costs shown upfront" />
              }
            />
          </>
        }
      />
    );
  }

  if (activeBenefitId === "terms") {
    return (
      <ProgramDetail
        id="terms"
        eyebrow={<PlaygroundLocalMsg defaultMessage="Payment terms" />}
        title={
          <PlaygroundLocalMsg defaultMessage="Order new inventory today, pay 60 days later" />
        }
        description={
          <PlaygroundLocalMsg defaultMessage="60-day payment terms help you buy the right amount of inventory while protecting your cash flow." />
        }
        ctaLabel="View payment terms"
        ctaUrl="https://www.faire.com/net-terms"
        heroTone="bg-fs-expressive-surface-blue"
        layoutVariant={layoutVariant}
        logo={
          <CreditCard
            className="text-fs-icon-primary h-(--slate-dimensions-12) w-(--slate-dimensions-12)"
            height="48"
            width="48"
          />
        }
        onNavigate={onNavigate}
        highlights={
          <>
            <DetailHighlight
              title={<PlaygroundLocalMsg defaultMessage="60 days" />}
              description={
                <PlaygroundLocalMsg defaultMessage="Time to sell products before payment is due" />
              }
            />
            <DetailHighlight
              title={<PlaygroundLocalMsg defaultMessage="Interest-free" />}
              description={
                <PlaygroundLocalMsg defaultMessage="No fee for paying with your available terms" />
              }
            />
            <DetailHighlight
              title={<PlaygroundLocalMsg defaultMessage="Ready to reuse" />}
              description={
                <PlaygroundLocalMsg defaultMessage="Paid invoices return that amount to your available terms" />
              }
            />
          </>
        }
      />
    );
  }

  if (activeBenefitId === "pos") {
    return (
      <ProgramDetail
        id="pos"
        eyebrow={<PlaygroundLocalMsg defaultMessage="POS integration" />}
        title={
          <PlaygroundLocalMsg defaultMessage="Add new products to your point of sale in just 5 minutes" />
        }
        description={
          <PlaygroundLocalMsg defaultMessage="Connect Shopify, Square, or Clover to move Faire products into your existing workflow." />
        }
        ctaLabel="Connect your point of sale"
        ctaUrl="https://www.faire.com/point-of-sale/about"
        heroTone="bg-fs-expressive-surface-green"
        layoutVariant={layoutVariant}
        logo={
          <span className="flex items-center gap-(--slate-spacing-element-small)">
            <ShopifyColored height="24" width="24" />
            <Square height="24" width="24" />
            <Clover height="24" width="24" />
          </span>
        }
        onNavigate={onNavigate}
        highlights={
          <>
            <DetailHighlight
              title={<PlaygroundLocalMsg defaultMessage="Add products" />}
              description={
                <PlaygroundLocalMsg defaultMessage="Send new Faire products to your POS after ordering" />
              }
            />
            <DetailHighlight
              title={
                <PlaygroundLocalMsg defaultMessage="Link existing items" />
              }
              description={
                <PlaygroundLocalMsg defaultMessage="Match Faire products to products already in your catalog" />
              }
            />
            <DetailHighlight
              title={<PlaygroundLocalMsg defaultMessage="Update inventory" />}
              description={
                <PlaygroundLocalMsg defaultMessage="Keep inventory counts current when linked products arrive" />
              }
            />
          </>
        }
      />
    );
  }

  if (activeBenefitId === "offers") {
    return (
      <OffersDetail layoutVariant={layoutVariant} onNavigate={onNavigate} />
    );
  }

  return (
    <ProgramDetail
      id="insider"
      eyebrow={<PlaygroundLocalMsg defaultMessage="Insider" />}
      title={
        <PlaygroundLocalMsg defaultMessage="Free shipping on thousands of unique brands" />
      }
      description={
        <PlaygroundLocalMsg defaultMessage="As an Insider member, you’ll take less risk trying new products and keep more savings to put back into your store." />
      }
      ctaLabel="Learn about Insider"
      ctaUrl="https://www.faire.com/insider"
      heroTone="bg-fs-program-surface-insider-primary"
      layoutVariant={layoutVariant}
      logo={<InsiderLogoLargeDefault height="26" width="94" />}
      onNavigate={onNavigate}
      highlights={
        <>
          <DetailHighlight
            title={<PlaygroundLocalMsg defaultMessage="Free shipping" />}
            description={
              <PlaygroundLocalMsg defaultMessage="A selection of brands personalized for your shop" />
            }
          />
          <DetailHighlight
            title={<PlaygroundLocalMsg defaultMessage="Member discounts" />}
            description={
              <PlaygroundLocalMsg defaultMessage="Exclusive savings available only to Insider members" />
            }
          />
          <DetailHighlight
            title={<PlaygroundLocalMsg defaultMessage="Early access" />}
            description={
              <PlaygroundLocalMsg defaultMessage="Shop online buying events and new brand arrivals first" />
            }
          />
        </>
      }
    />
  );
};

const ProgramDetail = ({
  id,
  eyebrow,
  title,
  description,
  ctaLabel,
  ctaUrl,
  heroTone,
  highlights,
  layoutVariant,
  logo,
  onNavigate,
}: {
  id: Exclude<BenefitId, "offers">;
  eyebrow: ReactNode;
  title: ReactNode;
  description: ReactNode;
  ctaLabel: string;
  ctaUrl: string;
  heroTone: string;
  highlights: ReactNode;
  layoutVariant: BenefitsMenuLayoutVariant;
  logo: ReactNode;
  onNavigate: () => void;
}): ReactNode => {
  const layout = MENU_LAYOUTS[layoutVariant];
  const localizedCtaLabel = usePlaygroundLocalMsg(ctaLabel);

  return (
    <article
      id={`benefit-panel-${id}`}
      aria-labelledby={`benefit-tab-${id}`}
      className={cn(detailPanelBaseCls, layout.detailPanelCls)}
      role="tabpanel"
    >
      <div className={cn(detailHeroBaseCls, heroTone, layout.detailHeroCls)}>
        <div className="flex h-full min-w-0 flex-col justify-between gap-(--slate-spacing-group-xlarge)">
          <div className="flex items-start justify-between gap-(--slate-spacing-group-large)">
            <div className="flex min-w-0 flex-col gap-(--slate-spacing-group-small)">
              <span className="type-fs-label-medium text-fs-text-subdued">
                {eyebrow}
              </span>
              <h3 className="type-fs-section-heading max-w-xl">{title}</h3>
            </div>
            <span className={cn(detailLogoBaseCls, layout.detailLogoCls)}>
              {logo}
            </span>
          </div>
          <p className="type-fs-paragraph max-w-2xl">{description}</p>
        </div>
      </div>

      <div
        className={cn(
          "grid grid-cols-1 gap-(--slate-spacing-element-default)",
          layout.highlightsContainerCls
        )}
      >
        {highlights}
      </div>

      <div
        className={cn("pt-(--slate-spacing-group-large)", {
          "mt-auto": layout.stickCtaToBottom,
        })}
      >
        <Button
          linkTo={ctaUrl}
          size="small"
          target="_blank"
          onClick={onNavigate}
        >
          {localizedCtaLabel}
        </Button>
      </div>
    </article>
  );
};

const DetailHighlight = ({
  title,
  description,
}: {
  title: ReactNode;
  description: ReactNode;
}): ReactNode => (
  <div className={detailHighlightCls}>
    <CheckOutline
      className="text-fs-icon-primary h-(--slate-dimensions-5) w-(--slate-dimensions-5)"
      height="20"
      width="20"
    />
    <div className="min-w-0">
      <p className="type-fs-paragraph-medium">{title}</p>
      <p className="type-fs-label text-fs-text-subdued">{description}</p>
    </div>
  </div>
);

interface OfferData {
  title: string;
  description: string;
  status: string;
}

const OFFERS: OfferData[] = [
  {
    title: "15% off your opening order",
    description: "Applied at checkout automatically",
    status: "Ends Mar 15",
  },
  {
    title: "Earn $200 back",
    description: "Spend $1,000 this month to unlock",
    status: "Ends Feb 28",
  },
  {
    title: "No minimum order",
    description: "First order from any brand, any size",
    status: "No end date",
  },
];

const OffersDetail = ({
  layoutVariant,
  onNavigate,
}: {
  layoutVariant: BenefitsMenuLayoutVariant;
  onNavigate: () => void;
}): ReactNode => {
  const layout = MENU_LAYOUTS[layoutVariant];

  return (
    <article
      id="benefit-panel-offers"
      aria-labelledby="benefit-tab-offers"
      className={cn(detailPanelBaseCls, layout.detailPanelCls)}
      role="tabpanel"
    >
      <div className="flex items-start justify-between gap-(--slate-spacing-group-large)">
        <div className="flex flex-col gap-(--slate-spacing-group-small)">
          <span className="type-fs-label-medium text-fs-text-subdued">
            <PlaygroundLocalMsg defaultMessage="Available now" />
          </span>
          <h3 className="type-fs-section-heading">
            <PlaygroundLocalMsg defaultMessage="Offers picked for your shop" />
          </h3>
          <p className="type-fs-paragraph text-fs-text-subdued">
            <PlaygroundLocalMsg defaultMessage="See your active discounts, rewards, and order incentives in one place." />
          </p>
        </div>
        <span
          className={cn(
            detailLogoBaseCls,
            layout.detailLogoCls,
            "bg-fs-expressive-surface-yellow"
          )}
        >
          <PriceTagDollar
            className="text-fs-icon-primary h-(--slate-dimensions-12) w-(--slate-dimensions-12)"
            height="48"
            width="48"
          />
        </span>
      </div>

      <ul className="grid gap-(--slate-spacing-group-large)">
        {OFFERS.map((offer) => (
          <LocalizedOffer key={offer.title} offer={offer} />
        ))}
      </ul>

      <div className="mt-auto pt-(--slate-spacing-group-xlarge)">
        <Button linkTo="#offers" size="small" onClick={onNavigate}>
          <PlaygroundLocalMsg defaultMessage="View all offers" />
        </Button>
      </div>
    </article>
  );
};

const LocalizedOffer = ({ offer }: { offer: OfferData }): ReactNode => {
  const title = usePlaygroundLocalMsg(offer.title);
  const description = usePlaygroundLocalMsg(offer.description);
  const status = usePlaygroundLocalMsg(offer.status);

  return (
    <li className={offerCardCls}>
      <div className="min-w-0">
        <p className="type-fs-paragraph-medium">{title}</p>
        <p className="type-fs-label text-fs-text-subdued">{description}</p>
      </div>
      <Tag label={status} variant="neutral" />
    </li>
  );
};

interface BenefitsMenuLayout {
  activeMenuItemCls: string;
  compactListItems: boolean;
  detailHeroCls: string;
  detailLogoCls: string;
  detailPanelCls: string;
  highlightsContainerCls: string;
  listHeadingCls: string;
  listSectionCls: string;
  menuItemIconCls: string;
  menuItemTitleCls: string;
  panelCls: string;
  showListIntroduction: boolean;
  showMenuItemArrow: boolean;
  showMenuItemDescription: boolean;
  stickCtaToBottom: boolean;
  useShortMenuTitles: boolean;
}

const MENU_LAYOUTS: Record<BenefitsMenuLayoutVariant, BenefitsMenuLayout> = {
  1: {
    activeMenuItemCls: "bg-fs-surface-secondary",
    compactListItems: true,
    detailHeroCls: "p-(--slate-spacing-element-large)",
    detailLogoCls: "h-(--slate-dimensions-12) w-(--slate-dimensions-14)",
    detailPanelCls:
      "gap-(--slate-spacing-group-large) p-(--slate-spacing-element-large)",
    highlightsContainerCls: "",
    listHeadingCls: "type-fs-label-medium",
    listSectionCls:
      "gap-(--slate-spacing-group-large) p-(--slate-spacing-element-large)",
    menuItemIconCls: "h-(--slate-dimensions-8) w-(--slate-dimensions-8)",
    menuItemTitleCls: "type-fs-label-medium block",
    panelCls: "h-[520px] w-[560px] grid-cols-[200px_minmax(0,1fr)]",
    showListIntroduction: true,
    showMenuItemArrow: false,
    showMenuItemDescription: false,
    stickCtaToBottom: true,
    useShortMenuTitles: false,
  },
  2: {
    activeMenuItemCls: "bg-fs-surface-secondary",
    compactListItems: true,
    detailHeroCls: "p-(--slate-spacing-element-large)",
    detailLogoCls: "h-(--slate-dimensions-12) w-(--slate-dimensions-14)",
    detailPanelCls:
      "gap-(--slate-spacing-group-default) p-(--slate-spacing-element-large)",
    highlightsContainerCls: "",
    listHeadingCls: "type-fs-subheading-medium",
    listSectionCls:
      "gap-(--slate-spacing-group-large) p-(--slate-spacing-element-large)",
    menuItemIconCls: "h-(--slate-dimensions-8) w-(--slate-dimensions-8)",
    menuItemTitleCls: "type-fs-label-medium block",
    panelCls: "h-[500px] w-[640px] grid-cols-[240px_minmax(0,1fr)]",
    showListIntroduction: true,
    showMenuItemArrow: true,
    showMenuItemDescription: true,
    stickCtaToBottom: true,
    useShortMenuTitles: false,
  },
  3: {
    activeMenuItemCls: "bg-fs-surface-secondary",
    compactListItems: true,
    detailHeroCls: "p-(--slate-spacing-element-large)",
    detailLogoCls: "h-(--slate-dimensions-12) w-(--slate-dimensions-14)",
    detailPanelCls:
      "gap-(--slate-spacing-group-large) p-(--slate-spacing-element-large)",
    highlightsContainerCls: "",
    listHeadingCls: "type-fs-label-medium",
    listSectionCls:
      "gap-(--slate-spacing-group-large) p-(--slate-spacing-element-default)",
    menuItemIconCls: "h-(--slate-dimensions-8) w-(--slate-dimensions-8)",
    menuItemTitleCls: "type-fs-label-medium block",
    panelCls: "h-[520px] w-[520px] grid-cols-[168px_minmax(0,1fr)]",
    showListIntroduction: true,
    showMenuItemArrow: false,
    showMenuItemDescription: false,
    stickCtaToBottom: false,
    useShortMenuTitles: true,
  },
  4: {
    activeMenuItemCls: "bg-fs-surface-secondary",
    compactListItems: true,
    detailHeroCls: "p-(--slate-spacing-element-large)",
    detailLogoCls: "h-(--slate-dimensions-12) w-(--slate-dimensions-14)",
    detailPanelCls:
      "gap-(--slate-spacing-group-default) p-(--slate-spacing-element-large)",
    highlightsContainerCls: cn(
      "[&>div]:bg-fs-surface-secondary [&>div]:rounded-fs-container-default",
      "[&>div]:p-(--slate-spacing-element-default)"
    ),
    listHeadingCls: "type-fs-subheading-medium",
    listSectionCls:
      "gap-(--slate-spacing-group-large) p-(--slate-spacing-element-large)",
    menuItemIconCls: "h-(--slate-dimensions-8) w-(--slate-dimensions-8)",
    menuItemTitleCls: "type-fs-label-medium block",
    panelCls: "h-[540px] w-[600px] grid-cols-[220px_minmax(0,1fr)]",
    showListIntroduction: true,
    showMenuItemArrow: false,
    showMenuItemDescription: false,
    stickCtaToBottom: false,
    useShortMenuTitles: false,
  },
  5: {
    activeMenuItemCls:
      "bg-fs-surface-secondary border-fs-action-border-default border-l-2",
    compactListItems: true,
    detailHeroCls: "p-(--slate-spacing-element-large)",
    detailLogoCls: "h-(--slate-dimensions-12) w-(--slate-dimensions-14)",
    detailPanelCls:
      "gap-(--slate-spacing-group-large) p-(--slate-spacing-element-large)",
    highlightsContainerCls: "",
    listHeadingCls: "type-fs-subheading-medium",
    listSectionCls:
      "gap-(--slate-spacing-group-large) p-(--slate-spacing-element-large)",
    menuItemIconCls: "h-(--slate-dimensions-8) w-(--slate-dimensions-8)",
    menuItemTitleCls: "type-fs-label-medium block",
    panelCls: "h-[500px] w-[580px] grid-cols-[210px_minmax(0,1fr)]",
    showListIntroduction: true,
    showMenuItemArrow: true,
    showMenuItemDescription: false,
    stickCtaToBottom: false,
    useShortMenuTitles: false,
  },
};

const menuPositionCls = cn(
  "absolute top-full right-[-104px] z-50",
  "pt-(--slate-spacing-element-small)"
);

const menuAnchorCls = cn(
  "bg-fs-surface-primary-inverse absolute top-(--slate-spacing-element-small) right-[112px] z-10",
  "h-px w-(--slate-dimensions-12)"
);

const menuPanelBaseCls = cn(
  "bg-fs-surface-primary border-fs-border-muted shadow-elevation-medium",
  "rounded-fs-container-default grid max-h-[calc(100vh-120px)] max-w-[calc(100vw-(2*var(--slate-spacing-layout-edge-desktop-default)))] overflow-auto border"
);

const menuListSectionBaseCls = cn(
  "flex min-w-0 flex-col",
  "border-fs-border-muted border-r"
);

const menuItemCls = cn(
  "focusable hover:bg-fs-surface-secondary active:bg-fs-surface-tertiary",
  "flex w-full cursor-pointer items-center gap-(--slate-spacing-element-large)",
  "rounded-fs-container-default px-(--slate-spacing-element-large) py-(--slate-spacing-element-medium)",
  "transition-colors motion-reduce:transition-none"
);

const menuItemIconCls = cn(
  "flex shrink-0 items-center justify-center",
  "rounded-fs-component-default h-(--slate-dimensions-12) w-(--slate-dimensions-12)"
);

const detailPanelBaseCls = "flex h-full min-w-0 flex-col";

const detailHeroBaseCls = "rounded-fs-container-default min-w-0";

const detailLogoBaseCls = cn(
  "bg-fs-surface-primary flex shrink-0 items-center justify-center",
  "rounded-fs-container-default"
);

const detailHighlightCls = cn(
  "flex min-w-0 items-start gap-(--slate-spacing-element-default)",
  "border-fs-border-muted border-b pb-(--slate-spacing-element-default) last:border-b-0 last:pb-0"
);

const offerCardCls = cn(
  "bg-fs-surface-secondary rounded-fs-container-default",
  "flex items-center justify-between gap-(--slate-spacing-group-large)",
  "px-(--slate-spacing-element-large) py-(--slate-spacing-element-medium)"
);
